import {ChecklistTarget} from '@components/checklist';
import {ConditionalError} from '@components/conditional-error';
import I9DocumentTitle from '@components/i9-document-title';
import DocumentDetailField from '@containers/form-i9/document-details-list/detail-field';
import {ExpiryDateField} from '@containers/form-i9/document-details-list/expiry-date-field';
import {
    renderPotentiallyEditableExpiryDateField,
    renderPrefilledField
} from '@containers/form-i9/document-details-list/field-content';
import {IssuingAuthorityField} from '@containers/form-i9/document-details-list/issuing-autority-field';
import {DocumentDetailGridItem} from '@containers/form-i9/document-details-list/styles';
import {getDocumentDetailsListTranslations} from '@containers/form-i9/document-details-list/translations';
import {BorderedGrid, EditableFormField} from '@containers/form-i9/styles';
import {OnboardingDocumentType} from '@dtos/document-type';
import {ISection2DocumentDetails} from '@models/onboarding-review/section2-document-details';
import {Section2ValidationErrors} from '@models/onboarding-review/section2-validation-errors';
import {isSocialSecurityCard} from '@models/onboarding-review/ssn-details';
import {CompleteI9PageTranslation} from '@screens/review-section2/complete-i9/complete-i9-page-translation';
import {
    createSection2DocumentSubmissionStore,
    createTranslationService,
    createValidateDocumentDetailService,
    createValidationPostEditStore
} from '@services/factories';
import {StringFormatService} from '@services/StringFormatService';
import {DocumentDetailValidationErrorTemplate} from '@services/validate-document-detail-service';
import {Section2DocumentDetailsStore} from '@stores/onboarding-review/section2-document-details-store';
import {VerticalLayout} from '@styles/spacing-styles';
import {cacheTranslations} from '@utils/cache-translations';
import {trimInvalidCharactersFromDocumentNumber} from '@utils/document-number-checks';
import {ISection2DocumentTypesValidation} from '@stores/onboarding-review/section2-document-types-validation-store';
import {observer, Observer} from 'mobx-react';
import * as React from 'react';
import {useState} from 'react';
import {I9DocExpiryDateOptionEnum} from '@swipejobs/i9-document-validations-react';

export const renderI9DocumentTitle = (value: string, documentTypesValidation?: ISection2DocumentTypesValidation[]) => { 

  const documentDetail = documentTypesValidation?.find(
    (item: ISection2DocumentTypesValidation) =>
      item.documentType === value
  );   
    return ( <I9DocumentTitle documentType={value}
                     fallbackName={value}
                     documentTitle={documentDetail?.title ?? ''}
                     /> );
}

interface IProps {
    className?: string;
    style?: React.CSSProperties;
    details?: ISection2DocumentDetails;
    detailsKey?: string;
    documentTitlePlaceholder: string;
    validationErrors: Record<Section2ValidationErrors, boolean>;
    allDocumentTypes: string[];
    documentTypesValidation: ISection2DocumentTypesValidation[];
}

export const LabelledDocumentDetails = observer((props: IProps) => {
    // as this maps to a single instance, this is permitted
    const documentSubmissionStore = createSection2DocumentSubmissionStore();
    const translations = getDocumentDetailsListTranslations();
    const completeI9Translations = cacheTranslations(
        createTranslationService(),
        CompleteI9PageTranslation,
        'completeI9'
    );
    const validationErrorTranslations = cacheTranslations(
        createTranslationService(),
        Section2ValidationErrors,
        'completeI9ValidationErrors'
    );
    const validateDocumentNumberService = createValidateDocumentDetailService();

    const validateDocumentNumber = (documentType: string, value: string, documentDetails: ISection2DocumentTypesValidation | undefined) => {
      return validateDocumentNumberService.validateDocumentNumber(
        documentType as OnboardingDocumentType,
        value,
        documentDetails
      );
    };

    const translateDocumentNumberValidationMessage = (messageTemplate: string): string => {
        const validationMessageTranslationMap = {
            [DocumentDetailValidationErrorTemplate.InvalidWithExample]: completeI9Translations.documentNumberInvalidWithExampleTemplate,
            [DocumentDetailValidationErrorTemplate.SSNMismatch]: completeI9Translations.documentNumberInvalidSSNMismatch,
            [DocumentDetailValidationErrorTemplate.SSNInvalidPattern]: completeI9Translations.documentNumberInvalidSSNPattern
        };

        return validationMessageTranslationMap[messageTemplate] ?? '';
    };

    const [documentNumberValidationPostEditStore] = useState(createValidationPostEditStore);
    const isSSN = isSocialSecurityCard(props.details?.documentType ?? '');

    const getIssuingAuthorities = (documentType: string | undefined) => {
        if ( documentType !== undefined ) {
            const documentDetails: ISection2DocumentTypesValidation | undefined =
        props.documentTypesValidation.find(
          (item: ISection2DocumentTypesValidation) =>
            item.documentType === documentType
        );
        return documentDetails?.issuingAuthority;
        }
        return [];
    }   

    const renderIssuingAuthority = () => {
        const documentDetail: ISection2DocumentTypesValidation | undefined =
                                            props.documentTypesValidation.find(
                                              (item: ISection2DocumentTypesValidation) =>
                                                item.documentType === props.details?.documentType
                                            );    

        const isIssuingAuthorityEmpty = (
          details: ISection2DocumentDetails,
          currentDocumentInfo: ISection2DocumentTypesValidation | undefined
        ): boolean => {
          return currentDocumentInfo?.freeTextAllowed
            ? details.issuingAuthority !== null && details.issuingAuthority !== ''
            : details.issuingAuthorityCode !== undefined &&
                details.issuingAuthorityCode !== null &&
                details.issuingAuthorityCode !== '';
        };
                
        const isValidIssuingAuthority = props.details !== undefined && (props.validationErrors.documentDetailMissing ? (
          props.validationErrors.documentDetailMissing && (isIssuingAuthorityEmpty(props.details, documentDetail))

        ) : (
          props.validationErrors.documentDetailMissing || (isIssuingAuthorityEmpty(props.details, documentDetail))));

    return (
      <ChecklistTarget
        name={`${props.detailsKey ?? ''}-issuing-authority`}
        valid={
          isValidIssuingAuthority
        }
        style={{ width: '100%', maxWidth: '100%' }}
        canRegister={props.details !== undefined}
      >
        <VerticalLayout innerSpacing='small'>
          <IssuingAuthorityField
            showLabel={true}
            details={props.details}
            issuingAuthorities={getIssuingAuthorities(
              props.details?.documentType
            )}
            onChange={(value) => {
              if (
                documentDetail &&
                !documentDetail?.freeTextAllowed &&
                documentDetail?.issuingAuthority?.length > 0
              ) {
                (
                  props.details as Section2DocumentDetailsStore
                ).setIssuingAuthorityCode(value);
                const issuingAuthority = documentDetail?.issuingAuthority.find(
                  (item) => item.code === value
                );
                (
                  props.details as Section2DocumentDetailsStore
                ).setIssuingAuthority(issuingAuthority?.displayName ?? '');
              } else {
                (
                  props.details as Section2DocumentDetailsStore
                ).setIssuingAuthority(value);
              }
            }}
            freeTextAllowed={documentDetail?.freeTextAllowed}
          />
          {props.details !== undefined ? (
            <ConditionalError
              condition={
                !isValidIssuingAuthority
              }
            >
              {validationErrorTranslations.documentDetailMissing}
            </ConditionalError>
          ) : null}
        </VerticalLayout>
      </ChecklistTarget>
    );
    }

    return (
        <BorderedGrid item={true}
                      container={true}
                      direction='column'
                      className={props.className}
                      style={props.style}>
            <DocumentDetailGridItem container={true}
                                    wrap={'nowrap'}>
                <DocumentDetailField details={props.details}
                                     fieldName={'documentType'}
                                     transformValue={renderI9DocumentTitle}
                                     documentTypesValidation={props.documentTypesValidation}
                                     label={translations.documentTitleLabel}
                                     placeholder={props.documentTitlePlaceholder}/>
            </DocumentDetailGridItem>
            <DocumentDetailGridItem container={true} wrap={'nowrap'}>
                        {renderIssuingAuthority()}
            </DocumentDetailGridItem>
            <DocumentDetailGridItem container={true} wrap={'nowrap'}>
                <DocumentDetailField
                    details={props.details}
                    fieldName={'number'}
                    label={translations.documentNumberLabel}
                    placeholder={translations.documentNumberPlaceholder}
                    renderFieldContent={(text: string, placeholder: string, value?: string) => {
                      const documentDetails: ISection2DocumentTypesValidation | undefined =
                      props.documentTypesValidation.find(
                        (item: ISection2DocumentTypesValidation) =>
                          item.documentType === props.details?.documentType
                      );
                        const documentNumberResult = validateDocumentNumber(
                            props.details?.documentType ?? '',
                            value ?? '',
                            documentDetails
                        );
               
                        const lastSubmittedDocumentNumber = documentSubmissionStore
                            .lastSubmittedDocumentNumbers.get(props.details?.documentId ?? '') ?? '';
                        const shouldShowSSNValidationMessage = isSSN
                            && documentNumberValidationPostEditStore.showValidationMessage;
                        const showDocumentNumberSection1MismatchError =
                            props.validationErrors.documentNumberDoesNotMatchSection1
                            && lastSubmittedDocumentNumber === props.details?.number
                            && shouldShowSSNValidationMessage;

                            const isValidDocumentNumber = props.details !== undefined && (props.validationErrors.documentDetailMissing ? (
                              props.validationErrors.documentDetailMissing && !props.details.number
                    
                            ) : (
                              props.validationErrors.documentDetailMissing || !props.details.number ));
                        return (
                            <ChecklistTarget
                                name={`${props.detailsKey ?? ''}-number`}
                                valid={!!props.details?.number && documentNumberResult.valid && !showDocumentNumberSection1MismatchError}
                                criteria={{
                                    fieldType: 'document-number',
                                    isSSN
                                }}
                                style={{ maxWidth: '100%' }}
                                canRegister={props.details !== undefined}
                            >
                                <VerticalLayout innerSpacing='small'>
                                    {props.details !== undefined ? (
                                        <React.Fragment><EditableFormField
                                  value={value}
                                  matchParentWidth={true}
                                  constrainToParent={true}
                                  placeholder={placeholder}
                                  onChange={(event) => {
                                    (props.details as Section2DocumentDetailsStore).setDocumentNumber(
                                      trimInvalidCharactersFromDocumentNumber(event.target.value)
                                    );

                                    documentNumberValidationPostEditStore.handleEdit();
                                  } }
                                  onBlur={() => {
                                    documentNumberValidationPostEditStore.handleBlur();
                                  } } />
                                  { documentDetails?.example &&
                                  <p>{translations.example}{documentDetails?.example}</p>} </React.Fragment>
                                    ) : null}
                                    {props.details === undefined
                                        ? renderPrefilledField(text)
                                        : null}
                                    {props.details !== undefined ? (
                                        <ConditionalError
                                            condition={
                                              isValidDocumentNumber
                                            }
                                        >
                                            {validationErrorTranslations.documentDetailMissing}
                                        </ConditionalError>
                                    ) : null}
                                    {props.details !== undefined ? (
                                        <ConditionalError
                                            condition={
                                                !!props.details.number
                                                && !documentNumberResult.valid
                                                && (isSSN
                                                    ? documentNumberValidationPostEditStore.showValidationMessage
                                                    : true)
                                            }
                                        >
                                          {!documentNumberResult.valid && documentNumberResult.messageTemplate ===
                                                                 DocumentDetailValidationErrorTemplate.InvalidWithExample && 
                                                                    documentNumberResult.templateArguments[0]
                                                                }
                                            {!documentNumberResult.valid && documentNumberResult.messageTemplate !==
                                                                 DocumentDetailValidationErrorTemplate.InvalidWithExample &&
                                                StringFormatService.format(
                                                    translateDocumentNumberValidationMessage(
                                                        documentNumberResult.messageTemplate
                                                    ),
                                                    documentNumberResult.templateArguments
                                                )}
                                        </ConditionalError>
                                    ) : null}
                                    {
                                        props.details !== undefined ? (
                                            <ConditionalError condition={
                                                !!props.details.number &&
                                                showDocumentNumberSection1MismatchError
                                            }>
                                                {validationErrorTranslations.documentNumberDoesNotMatchSection1}
                                            </ConditionalError>
                                        ) : null
                                    }
                                </VerticalLayout>
                            </ChecklistTarget>
                        );
                    }}
                />
            </DocumentDetailGridItem>
            <DocumentDetailGridItem container={true}
                                    wrap={'nowrap'}>
                <Observer>
                    {() => {
                        let expiryDateRequired:
                        | boolean
                        | undefined;
                      let showCheckbox:
                        | boolean
                        | undefined;

                        let validExpiryDateField: boolean = true; 

                      const documentDetails:
                        | ISection2DocumentTypesValidation
                        | undefined =
                        props.documentTypesValidation.find(
                          (
                            item: ISection2DocumentTypesValidation
                          ) =>
                            item.documentType ===
                            props.details?.documentType
                        );

                        switch (
                          documentDetails?.expiryDateOption
                        ) {
                          case I9DocExpiryDateOptionEnum.NO_EXPIRY_DATE:
                            expiryDateRequired = false;
                            showCheckbox = false;
                            validExpiryDateField = true;
                            break;
                          case I9DocExpiryDateOptionEnum.REQUIRED:
                            expiryDateRequired = true;
                            showCheckbox = false;
                            validExpiryDateField = ( props.details?.expiryDate !== null && props.details?.expiryDate !== '' ) 
                            && ( props.details?.isExpiryNaCheckboxChecked !== true ) ;
                            break;
                          case I9DocExpiryDateOptionEnum.NOT_APPLICABLE:
                            expiryDateRequired = false;
                            showCheckbox = true;
                            validExpiryDateField = ( props.details?.expiryDate === null || props.details?.expiryDate === '' ) 
                            && ( props.details?.isExpiryNaCheckboxChecked === true );
                            break;
                          default:
                            expiryDateRequired = true;
                            showCheckbox = true;
                            validExpiryDateField = ( props.details?.expiryDate !== null && props.details?.expiryDate !== '' ) 
                            || props.details?.isExpiryNaCheckboxChecked === true;
                        }

                        return (
                            <ChecklistTarget name={`${props.detailsKey ?? ''}-expiry-date`}
                                             valid={
                                                validExpiryDateField
                                             }
                                             canRegister={props.details !== undefined}
                                             style={{ width: '100%' }}
                            >
                                <VerticalLayout innerSpacing='small'>
                                    <ExpiryDateField
                                        showCheckbox={
                                            showCheckbox
                                          }
                                        details={props.details}
                                        showLabel={true}
                                        hideNoExpiryDate={expiryDateRequired}
                                        onNoExpiryDateToggled={hasNoExpiryDate => {
                                            const documentStore = (props.details as Section2DocumentDetailsStore);
                                            documentStore.setIsExpiryNaCheckboxChecked(
                                                hasNoExpiryDate
                                              );
                                            documentStore.setExpiryDate(
                                                hasNoExpiryDate ?
                                                    null :
                                                    validateDocumentNumberService.decideMinimumDocumentExpiry( documentDetails ) ?? ''
                                            );
                                        }}
                                        renderField={(
                                            renderPotentiallyEditableExpiryDateField(
                                                props.details !== undefined,
                                                value => (props.details as Section2DocumentDetailsStore)
                                                    .setExpiryDate(value),
                                                    validateDocumentNumberService.decideMinimumDocumentExpiry( documentDetails )
                                            )
                                        )}
                                    />
                                    {
                                        props.details !== undefined ? (
                                            <ConditionalError condition={
                                              !validExpiryDateField
                                            }>
                                                {validationErrorTranslations.documentDetailMissing}
                                            </ConditionalError>
                                        ) : null
                                    }
                                </VerticalLayout>
                            </ChecklistTarget>
                        );
                    }}
                </Observer>
            </DocumentDetailGridItem>
        </BorderedGrid>
    );
});



// WEBPACK FOOTER //
// ./src/containers/form-i9/document-details-list/labelled-document-details.tsx